import React, { useState } from "react";
import { Modal, Input, Button, Progress, Typography, notification, Tabs, Tag } from "antd";
import { LinkOutlined, CopyOutlined, MailOutlined, MessageOutlined, SendOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import { useQueryClient } from "react-query";
import {
  scrapeRestaumaticMenu,
  fetchLogoAsBlob,
  ScrapedMenu,
} from "../../../service/restaumatic-scraper";
import {
  createProductCategory,
  createProduct,
  createProductVariant,
  editCompanyBranch,
  uploadBranchLogo,
  uploadProductImage,
  createCompanyBranch,
  generateClaimLink,
  getCompany,
  sendOutreachEmail,
} from "../../../service";
import { colors, spacing } from "../../../styles/theme";

const { Text, Title } = Typography;
const { TextArea } = Input;

const GOOGLE_MAPS_API_KEY = "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs";
const MENU_APP_URL = "https://proste.menu";

// ─── Styled ──────────────────────────────────────────────────────────────────

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const PreviewBox = styled.div`
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const PreviewRow = styled.div`
  display: flex;
  gap: ${spacing.sm};
  font-size: 13px;
`;

const PreviewLabel = styled.span`
  color: ${colors.textSecondary};
  flex-shrink: 0;
  min-width: 110px;
`;

const PreviewValue = styled.span`
  color: ${colors.textPrimary};
  font-weight: 500;
`;

const DoneBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md} 0;
  text-align: center;
`;

const ClaimUrlBox = styled.div`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: ${spacing.sm} ${spacing.md};
  width: 100%;
`;

const ClaimUrl = styled.div`
  flex: 1;
  font-size: 12px;
  font-family: monospace;
  word-break: break-all;
  color: ${colors.primary};
`;

const MessageBox = styled.div`
  position: relative;
`;

const CopyButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateBranchSlug(name: string, id: number): string {
  const transliterated = name
    .replace(/ą/g, "a").replace(/Ą/g, "a")
    .replace(/ć/g, "c").replace(/Ć/g, "c")
    .replace(/ę/g, "e").replace(/Ę/g, "e")
    .replace(/ł/g, "l").replace(/Ł/g, "l")
    .replace(/ń/g, "n").replace(/Ń/g, "n")
    .replace(/ó/g, "o").replace(/Ó/g, "o")
    .replace(/ś/g, "s").replace(/Ś/g, "s")
    .replace(/ź/g, "z").replace(/Ź/g, "z")
    .replace(/ż/g, "z").replace(/Ż/g, "z");
  const base = transliterated
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `${base}-${id}`;
}

function addWhiteBackground(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error("canvas.toBlob failed"));
      }, "image/jpeg", 0.92);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image load failed"));
    };
    img.src = objectUrl;
  });
}

async function geocodeAddress(address: string): Promise<{ lat: number; lng: number; formattedAddress: string } | null> {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}&region=pl&language=pl`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status === "OK" && data.results.length > 0) {
    const r = data.results[0];
    return { lat: r.geometry.location.lat, lng: r.geometry.location.lng, formattedAddress: r.formatted_address };
  }
  return null;
}

function buildEmailMessage(claimUrl: string): string {
  return i18n.t("importBranchModal.emailMessageTemplate", { claimUrl });
}

function buildFacebookMessage(claimUrl: string): string {
  return i18n.t("importBranchModal.facebookMessageTemplate", { claimUrl });
}

// ─── Component ───────────────────────────────────────────────────────────────

interface ImportBranchModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = "input" | "preview" | "importing" | "outreach";

export const ImportBranchModal: React.FC<ImportBranchModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("input");
  const [url, setUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [scrapedData, setScrapedData] = useState<ScrapedMenu | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState(t("importBranchModal.defaultEmailSubject"));
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // ── Step 1: Scrape ──────────────────────────────────────────────────────────

  const handleScrape = async () => {
    if (!url.trim()) return;
    setIsScraping(true);
    setScrapeError(null);
    try {
      const data = await scrapeRestaumaticMenu(url.trim());
      setScrapedData(data);
      if (data.email) setEmailRecipient(data.email);
      setEmailSubject(t("importBranchModal.scrapedEmailSubject", { restaurantName: data.restaurantName }));
      setStep("preview");
    } catch (err: any) {
      setScrapeError(err?.message || t("importBranchModal.unknownError"));
    } finally {
      setIsScraping(false);
    }
  };

  // ── Step 2 → 3: Import ──────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!scrapedData) return;
    setStep("importing");

    const totalProducts = scrapedData.categories.reduce((s, c) => s + c.items.length, 0);
    const totalSteps = totalProducts + 3; // +create branch, +logo, +address
    let done = 0;

    const tick = (label: string) => {
      done++;
      setProgress({ current: done, total: totalSteps, label });
    };

    try {
      // 1. Create branch
      tick(t("importBranchModal.progressCreatingBranch"));
      const defaultOpeningHours = {
        monday: [{ from: "09:00", till: "17:00" }],
        tuesday: [{ from: "09:00", till: "17:00" }],
        wednesday: [{ from: "09:00", till: "17:00" }],
        thursday: [{ from: "09:00", till: "17:00" }],
        friday: [{ from: "09:00", till: "17:00" }],
        saturday: [],
        sunday: [],
      };
      const newBranch = await createCompanyBranch({
        name: scrapedData.restaurantName,
        openingHours: defaultOpeningHours,
      });
      const newBranchId: number = newBranch.id;

      // 2. Import categories + products
      for (const category of scrapedData.categories) {
        const newCategory = await createProductCategory({ name: category.name });

        for (const item of category.items) {
          const newProduct = await createProduct({
            name: item.name,
            description: item.description || "",
            productCategoryId: newCategory.id,
            isVisible: true,
            temporaryHidden: false,
            importBranchId: newBranchId,
          });

          if (newProduct?.id && item.priceInGrosze !== null) {
            await createProductVariant({
              productBaseId: newProduct.id,
              name: "Podstawowa",
              price: item.priceInGrosze,
              key: "default",
            });
          }

          if (newProduct?.id && item.imageUrl && !item.imageUrl.includes("3759b3df5982ce78756f289321cb79d3")) {
            try {
              const blob = await fetchLogoAsBlob(item.imageUrl);
              await uploadProductImage(newProduct.id, blob);
            } catch {
              // non-fatal
            }
          }

          tick(t("importBranchModal.progressImportingItem", { itemName: item.name }));
        }
      }

      await queryClient.invalidateQueries(["/product-categories"]);

      // 3. Logo
      tick(t("importBranchModal.progressUploadingLogo"));
      if (scrapedData.logoUrl) {
        try {
          const rawBlob = await fetchLogoAsBlob(scrapedData.logoUrl);
          const jpegBlob = await addWhiteBackground(rawBlob);
          await uploadBranchLogo(newBranchId, jpegBlob);
        } catch {
          // non-fatal
        }
      }

      // 4. Address + name + slug
      tick(t("importBranchModal.progressSavingAddress"));
      const generatedBranchSlug = generateBranchSlug(scrapedData.restaurantName, newBranchId);
      try {
        const body: any = {
          name: scrapedData.restaurantName,
          slug: generatedBranchSlug,
          bannerImage: "https://s3.eu-north-1.amazonaws.com/proste-menu-dev/3ec5ad39-2b0e-4b74-92c2-9eaf6cf7a0dd",
        };
        if (scrapedData.address) {
          const geo = await geocodeAddress(scrapedData.address);
          if (geo) {
            body.geographicPoint = { lat: geo.lat, lng: geo.lng };
            body.formattedAddress = geo.formattedAddress;
          }
        }
        await editCompanyBranch({ id: newBranchId, data: body });
      } catch {
        // non-fatal
      }

      // Odśwież listę branchy w BranchSwitcherze
      await queryClient.invalidateQueries(["/admin/company_branches"]);
      await queryClient.invalidateQueries(["/company-branches"]);

      // 5. Generate claim token
      const claimResult = await generateClaimLink(newBranchId);
      const token = claimResult.claimToken;

      // Build claim URL — fetch company slug, include branchSlug to skip branch picker
      const company = await queryClient.fetchQuery(["/company"], getCompany);
      const cSlug = (company as any)?.slug || "";
      const builtClaimUrl = `${MENU_APP_URL}/${cSlug}/${generatedBranchSlug}?claim=${token}`;

      setClaimUrl(builtClaimUrl);
      setStep("outreach");
    } catch (err: any) {
      notification.error({
        message: t("importBranchModal.importErrorTitle"),
        description: err?.response?.data?.message || err?.message || t("importBranchModal.tryAgain"),
        placement: "bottomRight",
      });
      setStep("preview");
    }
  };

  // ── Copy helpers ─────────────────────────────────────────────────────────────

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    notification.success({ message: t("importBranchModal.copiedNotification", { label }), placement: "bottomRight", duration: 2 });
  };

  // ── Reset ────────────────────────────────────────────────────────────────────

  const handleClose = () => {
    setStep("input");
    setUrl("");
    setScrapeError(null);
    setScrapedData(null);
    setProgress({ current: 0, total: 0, label: "" });
    setClaimUrl(null);
    setEmailRecipient("");
    setEmailSubject(t("importBranchModal.defaultEmailSubject"));
    setIsSendingEmail(false);
    onClose();
  };

  const handleSendEmail = async () => {
    if (!emailRecipient.trim() || !claimUrl) return;
    setIsSendingEmail(true);
    try {
      await sendOutreachEmail(emailRecipient.trim(), emailSubject, emailMsg);
      notification.success({
        message: t("importBranchModal.emailSentTitle"),
        description: t("importBranchModal.emailSentDescription", { recipient: emailRecipient.trim() }),
        placement: "bottomRight",
      });
    } catch (err: any) {
      notification.error({
        message: t("importBranchModal.emailSendErrorTitle"),
        description: err?.response?.data?.message || err?.message || t("importBranchModal.tryAgain"),
        placement: "bottomRight",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const restaurantName = scrapedData?.restaurantName || t("importBranchModal.defaultRestaurantName");
  const emailMsg = claimUrl ? buildEmailMessage(claimUrl) : "";
  const fbMsg = claimUrl ? buildFacebookMessage(claimUrl) : "";

  const totalProducts = scrapedData?.categories.reduce((s, c) => s + c.items.length, 0) ?? 0;
  const totalImages = scrapedData?.categories.reduce((s, c) => s + c.items.filter((i) => i.imageUrl).length, 0) ?? 0;

  return (
    <Modal
      open={open}
      onCancel={step === "importing" ? undefined : handleClose}
      closable={step !== "importing"}
      maskClosable={step !== "importing"}
      title={
        step === "input" ? t("importBranchModal.titleInput") :
          step === "preview" ? t("importBranchModal.titlePreview", { restaurantName }) :
            step === "importing" ? t("importBranchModal.titleImporting") :
              t("importBranchModal.titleOutreach")
      }
      footer={
        step === "input" ? (
          <Button type="primary" onClick={handleScrape} loading={isScraping} disabled={!url.trim()}>
            {t("importBranchModal.fetchMenuButton")}
          </Button>
        ) : step === "preview" ? (
          <>
            <Button onClick={() => setStep("input")}>{t("importBranchModal.backButton")}</Button>
            <Button type="primary" onClick={handleImport}>
              {t("importBranchModal.importAndGenerateButton")}
            </Button>
          </>
        ) : step === "outreach" ? (
          <Button type="primary" onClick={handleClose}>{t("importBranchModal.closeButton")}</Button>
        ) : null
      }
      width={600}
    >
      {/* ── Step 1: URL input ── */}
      {step === "input" && (
        <Step>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t("importBranchModal.inputDescription")}
          </Text>
          <Input
            prefix={<LinkOutlined />}
            placeholder="https://www.restaumatic.com/site/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onPressEnter={handleScrape}
            size="large"
            status={scrapeError ? "error" : undefined}
          />
          {scrapeError && (
            <Text type="danger" style={{ fontSize: 13 }}>{scrapeError}</Text>
          )}
        </Step>
      )}

      {/* ── Step 2: Preview ── */}
      {step === "preview" && scrapedData && (
        <Step>
          <PreviewBox>
            <PreviewRow>
              <PreviewLabel>{t("importBranchModal.previewNameLabel")}</PreviewLabel>
              <PreviewValue>{scrapedData.restaurantName}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>{t("importBranchModal.previewCategoriesLabel")}</PreviewLabel>
              <PreviewValue>{scrapedData.categories.length}</PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>{t("importBranchModal.previewProductsLabel")}</PreviewLabel>
              <PreviewValue>{totalProducts}</PreviewValue>
            </PreviewRow>
            {totalImages > 0 && (
              <PreviewRow>
                <PreviewLabel>{t("importBranchModal.previewImagesLabel")}</PreviewLabel>
                <PreviewValue>{totalImages} {t("importBranchModal.previewImagesSuffix")}</PreviewValue>
              </PreviewRow>
            )}
            {scrapedData.logoUrl && (
              <PreviewRow>
                <PreviewLabel>{t("importBranchModal.previewLogoLabel")}</PreviewLabel>
                <PreviewValue>{t("importBranchModal.previewLogoFound")}</PreviewValue>
              </PreviewRow>
            )}
            {scrapedData.address && (
              <PreviewRow>
                <PreviewLabel>{t("importBranchModal.previewAddressLabel")}</PreviewLabel>
                <PreviewValue>{scrapedData.address}</PreviewValue>
              </PreviewRow>
            )}
            <PreviewRow>
              <PreviewLabel>{t("importBranchModal.previewEmailLabel")}</PreviewLabel>
              <PreviewValue>
                {scrapedData.email
                  ? <><Tag color="green" style={{ marginRight: 4 }}>{t("importBranchModal.previewFound")}</Tag>{scrapedData.email}</>
                  : <Tag color="default">{t("importBranchModal.previewNotFound")}</Tag>
                }
              </PreviewValue>
            </PreviewRow>
            <PreviewRow>
              <PreviewLabel>{t("importBranchModal.previewFacebookLabel")}</PreviewLabel>
              <PreviewValue>
                {scrapedData.facebookUrl
                  ? <><Tag color="blue" style={{ marginRight: 4 }}>{t("importBranchModal.previewFound")}</Tag>{scrapedData.facebookUrl}</>
                  : <Tag color="default">{t("importBranchModal.previewNotFound")}</Tag>
                }
              </PreviewValue>
            </PreviewRow>
          </PreviewBox>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("importBranchModal.previewFooterNote")}
          </Text>
        </Step>
      )}

      {/* ── Step 3: Importing ── */}
      {step === "importing" && (
        <Step style={{ alignItems: "center", padding: `${spacing.lg} 0` }}>
          <Progress
            type="circle"
            percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}
            size={120}
          />
          <Text style={{ fontSize: 13, color: colors.textSecondary, textAlign: "center" }}>
            {progress.label || t("importBranchModal.progressPreparing")}
          </Text>
        </Step>
      )}

      {/* ── Step 4: Outreach ── */}
      {step === "outreach" && claimUrl && (
        <Step>
          <DoneBox>
            <span style={{ fontSize: 40 }}>{"🎉"}</span>
            <Title level={5} style={{ margin: 0 }}>
              {t("importBranchModal.outreachDoneTitle")}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("importBranchModal.outreachDoneDescription")}
            </Text>
          </DoneBox>

          <Text strong style={{ fontSize: 13 }}>{t("importBranchModal.menuLinkLabel")}</Text>
          <ClaimUrlBox>
            <ClaimUrl>{claimUrl}</ClaimUrl>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copy(claimUrl, t("importBranchModal.copyLabelLink"))}
            >
              {t("importBranchModal.copyButton")}
            </Button>
          </ClaimUrlBox>

          <Tabs
            items={[
              {
                key: "email",
                label: (
                  <span><MailOutlined style={{ marginRight: 6 }} />{"Email"}</span>
                ),
                children: (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Input
                      addonBefore={t("importBranchModal.emailToAddon")}
                      placeholder={"email@restauracja.pl"}
                      value={emailRecipient}
                      onChange={(e) => setEmailRecipient(e.target.value)}
                      size="small"
                      style={{ fontSize: 13 }}
                    />
                    <Input
                      addonBefore={t("importBranchModal.emailSubjectAddon")}
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      size="small"
                      style={{ fontSize: 13 }}
                    />
                    <MessageBox>
                      <TextArea
                        value={emailMsg}
                        autoSize={{ minRows: 10, maxRows: 18 }}
                        readOnly
                        style={{ fontSize: 12, fontFamily: "monospace", paddingRight: 80 }}
                      />
                      <CopyButton
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copy(emailMsg, t("importBranchModal.copyLabelEmailMessage"))}
                      >
                        {t("importBranchModal.copyButton")}
                      </CopyButton>
                    </MessageBox>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={isSendingEmail}
                      disabled={!emailRecipient.trim()}
                      onClick={handleSendEmail}
                    >
                      {t("importBranchModal.sendEmailButton")}
                    </Button>
                  </div>
                ),
              },
              {
                key: "facebook",
                label: (
                  <span><MessageOutlined style={{ marginRight: 6 }} />{"Facebook"}</span>
                ),
                children: (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {scrapedData?.facebookUrl ? (
                      <Button
                        icon={<MessageOutlined />}
                        href={scrapedData.facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ width: "100%", textAlign: "left" }}
                        onClick={() => copy(fbMsg, t("importBranchModal.copyLabelFacebookMessage"))}
                      >
                        {t("importBranchModal.openFacebookProfile")}
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                          {scrapedData.facebookUrl}
                        </Text>
                      </Button>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("importBranchModal.facebookNotFound")}
                      </Text>
                    )}
                    <MessageBox>
                      <TextArea
                        value={fbMsg}
                        autoSize={{ minRows: 10, maxRows: 18 }}
                        readOnly
                        style={{ fontSize: 12, fontFamily: "monospace", paddingRight: 80 }}
                      />
                      <CopyButton
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copy(fbMsg, t("importBranchModal.copyLabelFacebookMessage"))}
                      >
                        {t("importBranchModal.copyButton")}
                      </CopyButton>
                    </MessageBox>
                  </div>
                ),
              },
            ]}
          />
        </Step>
      )}
    </Modal>
  );
};
